import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DynamicPage = () => {
  const { '*': path } = useParams();
  const { language } = useLanguage();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [path]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/pages/${path}`);
      setPage(response.data);
    } catch (error) {
      console.error('Failed to fetch page:', error);
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 pb-20 px-4 flex items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl font-heading font-bold text-foreground mb-4">
              {language === 'en' ? 'Page Not Found' : 'पृष्ठ नहीं मिला'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'en' 
                ? 'This page is being set up. Please check back later or contact the administrator.'
                : 'यह पृष्ठ सेट अप किया जा रहा है। कृपया बाद में देखें या व्यवस्थापक से संपर्क करें।'}
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Render based on content_type
  const renderContent = () => {
    switch (page.content_type) {
      case 'services':
      case 'cards':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {page.items?.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl border border-slate-100 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={language === 'en' ? item.title_en : item.title_hi}
                    className="w-full h-48 object-cover rounded-xl mb-4"
                  />
                )}
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  {language === 'en' ? item.title_en : item.title_hi}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'en' ? item.description_en : item.description_hi}
                </p>
              </div>
            ))}
          </div>
        );

      case 'gallery':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {page.items?.map((item, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-xl">
                <img
                  src={item.image_url}
                  alt={language === 'en' ? item.title_en : item.title_hi}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        );

      case 'text':
        return (
          <div className="max-w-4xl mx-auto prose prose-lg">
            {page.items?.map((item, index) => (
              <div key={index} className="mb-8">
                {item.title_en && (
                  <h3 className="text-2xl font-heading font-semibold text-foreground mb-4">
                    {language === 'en' ? item.title_en : item.title_hi}
                  </h3>
                )}
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {language === 'en' ? item.description_en : item.description_hi}
                </p>
              </div>
            ))}
          </div>
        );

      default:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {page.items?.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl border border-slate-100 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={language === 'en' ? item.title_en : item.title_hi}
                    className="w-full h-48 object-cover rounded-xl mb-4"
                  />
                )}
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                  {language === 'en' ? item.title_en : item.title_hi}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'en' ? item.description_en : item.description_hi}
                </p>
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground mb-4">
              {language === 'en' ? page.title_en : page.title_hi}
            </h1>
            {page.subtitle_en && (
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {language === 'en' ? page.subtitle_en : page.subtitle_hi}
              </p>
            )}
          </div>

          {page.items?.length > 0 ? (
            renderContent()
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
              <p className="text-muted-foreground">
                {language === 'en' 
                  ? 'Content is being prepared. Please check back soon!'
                  : 'सामग्री तैयार की जा रही है। कृपया जल्द ही देखें!'}
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DynamicPage;
